import { useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import {
  Button,
  Dialog,
  Divider,
  Dropdown,
  Group,
  IconCaretDown,
  IconCaretRight,
  IconGear,
  IconMinusCircle,
  IconThreeDotsVer,
  IconWarning,
  Table,
  Token,
  Tooltip,
  Typography,
  useDropdownState,
  useModalPortal,
  Wrapper
} from '@screentone/core';

import { useStructureEditContext } from 'contexts/structure-edit/StructureEditContext';
import { StructureGroup, StructureGroupConfig } from 'data/generated/graphql';
import { DND_GROUP_ID_PREFIX, DND_TYPE_MODULE, FlatSlot, swapAdjacentItems } from 'utils/layoutUtils';
import { AddModuleButton } from './AddModuleButton';
import styles from './ModuleGroup.module.scss';
import { ModuleItem } from './ModuleItem';
import { ReorderControls } from './ReorderControls';
import { GroupOptionsModal } from '../modals/GroupOptionsModal';

interface ModuleGroupProps {
  index: number;
  totalGroups: number;
  groupId: string;
  groupLabel?: string;
  layoutGroup?: StructureGroup;
  modules: FlatSlot[];
  availableTreatmentTypes: string[];
}

interface LimitsRow {
  label: string;
  current: number;
  min?: number | null;
  max?: number | null;
}

function buildLimitsRows(config: StructureGroupConfig, modules: FlatSlot[]): LimitsRow[] {
  const rows: LimitsRow[] = [{ label: 'Total modules', current: modules.length, min: config.min, max: config.max }];

  const ttCount: Record<string, number> = {};
  modules.forEach((m) => {
    ttCount[m.ttKey] = (ttCount[m.ttKey] ?? 0) + 1;
  });

  const allTTs = new Set<string>([
    ...Object.keys(ttCount),
    ...(config.treatmentTypeLimits ?? []).map((l) => l.ref).filter(Boolean)
  ]);
  allTTs.forEach((tt) => {
    const limit = (config.treatmentTypeLimits ?? []).find((l) => l.ref === tt);
    rows.push({ label: tt, current: ttCount[tt] ?? 0, min: limit?.min, max: limit?.max });
  });
  return rows;
}

function limitLabel(min?: number | null, max?: number | null): string {
  if (min != null && max != null) return `min ${min} · max ${max}`;
  if (min != null) return `min ${min}`;
  if (max != null) return `max ${max}`;
  return '';
}

export const ModuleGroup = ({
  index,
  totalGroups,
  groupId,
  groupLabel,
  layoutGroup,
  modules,
  availableTreatmentTypes
}: ModuleGroupProps) => {
  const { layout, modules: allModules, updateGroup, removeGroup, reorderGroups } = useStructureEditContext();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [limitsOpen, setLimitsOpen] = useState(false);

  const handleMoveUp = () => {
    if (index > 0) reorderGroups(swapAdjacentItems(layout.structure?.groups ?? [], index, 'up'));
  };

  const handleMoveDown = () => {
    if (index < totalGroups - 1) reorderGroups(swapAdjacentItems(layout.structure?.groups ?? [], index, 'down'));
  };

  const { open: dropdownOpen, setOpen: setDropdownOpen, componentRef: dropdownRef } = useDropdownState();
  const { renderNode } = useModalPortal();

  const droppableId = `${DND_GROUP_ID_PREFIX}${groupId}`;
  const effectiveGroup: StructureGroup | undefined =
    layoutGroup ?? (layout.structure?.groups ?? []).find((g) => g.id === groupId);

  const limitsRows: LimitsRow[] = effectiveGroup ? buildLimitsRows(effectiveGroup.config, modules) : [];
  const hasLimits = limitsRows.length > 0;
  const hasViolations = limitsRows.some(
    (r) => (r.min != null && r.current < r.min) || (r.max != null && r.current > r.max)
  );

  const groupContent = (dragHandleProps?: object) => (
    <div className={styles.groupRow} data-group-id={groupId} data-testid="group-container">
      <div className={styles.groupWrapper}>
        <Droppable droppableId={droppableId} type={DND_TYPE_MODULE}>
          {(provided) => (
            <div data-testid="group-wrapper-inner" ref={provided.innerRef} {...provided.droppableProps}>
              <Group data-testid="group-header" align="space-between" margin={{ bottom: 'xs' }}>
                <Group gap="xs" align="center">
                  <Button
                    tertiary
                    icon={collapsed ? IconCaretRight : IconCaretDown}
                    onClick={() => setCollapsed((v) => !v)}
                    aria-label={collapsed ? 'Expand group' : 'Collapse group'}
                  />
                  <Typography
                    data-testid="group-label"
                    className={styles.groupLabel}
                    variant="label2"
                    size="sm"
                    color="asphalt"
                    weight="bold"
                    margin={{ bottom: 'none' }}
                  >
                    {groupLabel ?? groupId}
                    {effectiveGroup?.config.showModuleCount && (
                      <Token data-testid="group-count-token" margin={{ bottom: 'none', left: 'xs' }}>
                        {`${modules.length} ${modules.length === 1 ? 'module' : 'modules'}`}
                      </Token>
                    )}
                  </Typography>
                </Group>

                <Group gap="xs" align="center">
                  {hasViolations && (
                    <Tooltip>
                      <Tooltip.Content position="left">
                        Check Module limits below — some requirements are not met.
                      </Tooltip.Content>
                      <Tooltip.Trigger>
                        <IconWarning color="lava" />
                      </Tooltip.Trigger>
                    </Tooltip>
                  )}
                  <Dropdown
                    open={dropdownOpen}
                    position="right"
                    trigger={<IconThreeDotsVer color="asphalt" />}
                    onToggle={() => setDropdownOpen(!dropdownOpen)}
                    componentRef={dropdownRef}
                    padding={{ all: 'none' }}
                  >
                    <Wrapper padding={{ all: 'md' }}>
                      <Button
                        tertiary
                        fullWidth
                        icon={IconGear}
                        onClick={() => {
                          setDropdownOpen(false);
                          setOptionsOpen(true);
                        }}
                      >
                        Group options
                      </Button>
                    </Wrapper>
                    <Divider />
                    <Wrapper padding={{ all: 'md' }}>
                      <Button
                        tertiary
                        fullWidth
                        icon={IconMinusCircle}
                        color="lava"
                        onClick={() => {
                          setDropdownOpen(false);
                          setConfirmRemoveOpen(true);
                        }}
                      >
                        Remove group
                      </Button>
                    </Wrapper>
                  </Dropdown>
                </Group>
              </Group>

              {!collapsed && modules.length === 0 && (
                <Typography variant="note" color="asphalt" margin={{ bottom: 'sm' }}>
                  No modules in this group
                </Typography>
              )}
              {!collapsed && <AddModuleButton groupId={groupId} insertIndex={0} />}
              {!collapsed &&
                modules.map((module, localIndex) => {
                  const foundIndex = allModules.findIndex((m) => m === module);
                  const globalIndex = foundIndex !== -1 ? foundIndex : localIndex;
                  return (
                    <div
                      key={`${module.ttKey}-${localIndex}`}
                      className={styles.childWrapper}
                      data-testid="group-child"
                      data-child-index={localIndex}
                    >
                      <ModuleItem
                        module={module}
                        moduleIndex={globalIndex}
                        localIndex={localIndex}
                        isFirst={localIndex === 0}
                        isLast={localIndex === modules.length - 1}
                        groupAllowedTreatmentTypes={effectiveGroup?.config.allowedTreatmentTypes ?? []}
                      />
                      <AddModuleButton groupId={groupId} insertIndex={localIndex + 1} />
                    </div>
                  );
                })}
              {provided.placeholder}
              {!collapsed && hasLimits && (
                <Wrapper className={styles.limitsPanel}>
                  <Button
                    tertiary
                    icon={limitsOpen ? IconCaretDown : IconCaretRight}
                    onClick={() => setLimitsOpen((v) => !v)}
                    className={styles.fullWidthButton}
                  >
                    <Typography variant="label2" weight="bold" margin={{ bottom: 'none' }}>
                      Module limits
                    </Typography>
                  </Button>
                  {limitsOpen && (
                    <>
                      <Typography variant="note" color="asphalt" margin={{ bottom: 'xs' }}>
                        Limits are configurable in Group options.
                      </Typography>
                      <Table fullWidth>
                        <Table.Head>
                          <Table.Row>
                            <Table.Cell isLabel>Treatment type</Table.Cell>
                            <Table.Cell isLabel className={styles.limitsCountCell}>
                              Current
                            </Table.Cell>
                            <Table.Cell isLabel className={styles.limitsRequiredCell}>
                              Required
                            </Table.Cell>
                          </Table.Row>
                        </Table.Head>
                        <Table.Body>
                          {limitsRows.map((row, rowIndex) => {
                            const outOfBounds =
                              (row.min != null && row.current < row.min) || (row.max != null && row.current > row.max);
                            const isTotal = rowIndex === 0;
                            const color = outOfBounds ? 'lava' : undefined;
                            return (
                              <Table.Row key={row.label}>
                                <Table.Cell>
                                  <Typography
                                    variant="note"
                                    weight={isTotal ? 'bold' : undefined}
                                    color={color}
                                    margin={{ bottom: 'none' }}
                                  >
                                    {row.label}
                                  </Typography>
                                </Table.Cell>
                                <Table.Cell className={styles.limitsDataCell}>
                                  <Typography
                                    variant="note"
                                    weight={isTotal ? 'bold' : undefined}
                                    color={color}
                                    margin={{ bottom: 'none' }}
                                  >
                                    {row.current}
                                    {outOfBounds ? ' ⚠' : ''}
                                  </Typography>
                                </Table.Cell>
                                <Table.Cell>
                                  <Typography
                                    variant="note"
                                    weight={isTotal ? 'bold' : undefined}
                                    color={color}
                                    margin={{ bottom: 'none' }}
                                  >
                                    {limitLabel(row.min, row.max) || '—'}
                                  </Typography>
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table>
                    </>
                  )}
                </Wrapper>
              )}
            </div>
          )}
        </Droppable>
      </div>

      {dragHandleProps && (
        <ReorderControls
          isFirst={index === 0}
          isLast={index >= totalGroups - 1}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          dragHandleProps={dragHandleProps}
          dragHandleAriaLabel="Drag group"
        />
      )}
    </div>
  );

  return (
    <>
      <Draggable draggableId={`${DND_GROUP_ID_PREFIX}drag-${groupId}`} index={index}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.draggableProps}>
            {groupContent(provided.dragHandleProps ?? undefined)}
          </div>
        )}
      </Draggable>
      {optionsOpen && effectiveGroup && (
        <GroupOptionsModal
          group={effectiveGroup}
          availableTreatmentTypes={availableTreatmentTypes}
          modules={modules}
          onSave={(patch) => {
            updateGroup(groupId, patch);
            setOptionsOpen(false);
          }}
          onDismiss={() => setOptionsOpen(false)}
        />
      )}
      {confirmRemoveOpen && (
        <Dialog status="open" onDismiss={() => setConfirmRemoveOpen(false)} focusTrap={false} renderNode={renderNode}>
          <Dialog.Title>Remove group</Dialog.Title>
          <Dialog.Content>
            <Wrapper padding={{ all: 'md' }}>
              <Typography variant="bodytext">
                Remove <strong>{groupLabel ?? groupId}</strong>?
                {modules.length > 0 && (
                  <>
                    {' '}
                    This will also remove the {modules.length} module{modules.length !== 1 ? 's' : ''} inside it.
                  </>
                )}
              </Typography>
            </Wrapper>
          </Dialog.Content>
          <Dialog.Actions>
            <Button secondary onClick={() => setConfirmRemoveOpen(false)}>
              Cancel
            </Button>
            <Button
              primary
              color="lava"
              onClick={() => {
                removeGroup(groupId);
                setConfirmRemoveOpen(false);
              }}
            >
              Remove
            </Button>
          </Dialog.Actions>
        </Dialog>
      )}
    </>
  );
};
